/**
 * src/pages/CriticalThinking/sections/SkillSetSection.tsx
 * Section 2: The Skill Set — Teaching core disciplines of critical thinking.
 * Covers:
 * - Clarifying the question
 * - Claims vs evidence
 * - Observation vs interpretation
 * - Hidden premises
 * - Alternative explanations
 * - Counterexamples
 * - Relevance & reliability of evidence
 * - Confidence vs certainty
 * - Underspecified claims
 * - What would change my mind
 * - Avoiding premature conclusions
 * - Calibrating confidence & revising beliefs
 * - "False" vs "Not established" & "Insufficient information" as a legitimate conclusion
 */

import React from "react";
import { KSection } from "@/components/knowledge/KSection";
import { LessonStep } from "@/components/knowledge/LessonStep";
import { Deeper } from "@/components/knowledge/Deeper";
import { StatusBadge } from "@/components/knowledge/StatusBadge";
import { Term } from "@/components/knowledge/Term";
import { CheckCircle2, ShieldAlert, Scale, HelpCircle } from "lucide-react";
export function SkillSetSection() {
  return <KSection id="skill-set" eyebrow="Epistemic Toolkit" title="The Foundations of Critical Thinking" intro="Critical thinking is not cynical contrarianism, aggressive debate, or finding clever ways to win arguments. It is the disciplined, practical habit of evaluating how well our beliefs are grounded in evidence, clarifying ambiguous claims, making hidden assumptions visible, and calibrating our confidence with intellectual humility.">
      <div className="space-y-8">
        {/* Step 1: The Central Question */}
        <LessonStep kind="question" title="What does it actually mean to think critically?">
          <p>
            When someone asserts a bold headline, a medical guarantee, a political forecast, or a workplace ultimatum, what mental steps separate a disciplined thinker from a credulous follower or an irritable skeptic?
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            <div className="p-4 rounded-lg border border-border/60 bg-card/40">
              <span className="font-mono text-xs text-destructive font-bold uppercase block mb-1">
                What critical thinking is NOT:
              </span>
              <ul className="text-xs text-muted-foreground space-y-1.5 list-disc pl-4">
                <li>Cynicism or reflexively disbelieving everything you hear.</li>
                <li>Being argumentative or attacking the person speaking.</li>
                <li>Accumulating clever rhetorical tricks to never admit error.</li>
                <li>Equating all viewpoints as equally valid or equally arbitrary.</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg border border-primary/40 bg-card/60">
              <span className="font-mono text-xs text-primary font-bold uppercase block mb-1">
                What critical thinking IS:
              </span>
              <ul className="text-xs text-muted-foreground space-y-1.5 list-disc pl-4">
                <li>Clarifying exactly what is being asserted before evaluating it.</li>
                <li>Distinguishing between direct observation and subjective interpretation.</li>
                <li>Inspecting the hidden assumptions bridging a <Term id="premise" /> to a <Term id="conclusion" />.</li>
                <li>Accepting that "insufficient information" is often the most honest answer.</li>
              </ul>
            </div>
          </div>
        </LessonStep>

        {/* Step 2: The Core Skill Stack */}
        <LessonStep kind="explanation" title="The Twelve Core Analytical Disciplines">
          <p className="mb-4">
            A rigorous critical evaluation moves systematically through twelve mental checkpoints:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
            {[{
            title: "1. Clarifying the Question",
            desc: "Never debate an underspecified target. Ask: What exact assertion or decision is on the table?"
          }, {
            title: "2. Claims vs Evidence",
            desc: "An assertion is not its own justification. A claim is what someone wants you to accept; evidence is the independent reason to believe it."
          }, {
            title: "3. Observation vs Interpretation",
            desc: "Separate what was physically seen or measured from the explanatory story added onto it."
          }, {
            title: "4. Hidden Premises",
            desc: "Most everyday arguments are enthymemes with unstated bridges. Identify the unspoken premise that must be true for the argument to work."
          }, {
            title: "5. Alternative Explanations",
            desc: "Always ask: What else could account for this observed result? (Placebo effect, regression to mean, seasonal cycles, sampling bias)."
          }, {
            title: "6. Counterexamples",
            desc: "Test the universality of categorical statements ('All X are Y') by actively seeking a single valid counterexample."
          }, {
            title: "7. Evidence Reliability",
            desc: "Evaluate source incentives, methodological controls, sample sizes, and whether the evidence is cherry-picked or peer-reviewed."
          }, {
            title: "8. Confidence vs Certainty",
            desc: "Certainty is an emotional feeling; confidence is a calibrated probability. Treat beliefs as degrees of credence, not binary switches."
          }, {
            title: "9. Underspecified Claims",
            desc: "Identify vague metrics like 'boosts health' or 'improves efficiency' that dodge falsifiability by never pinning down measurable targets."
          }, {
            title: "10. What Would Change My Mind?",
            desc: "If no conceivable observation or measurement could lower your confidence in an idea, you are holding dogma, not an empirical belief."
          }, {
            title: "11. Avoiding Premature Closure",
            desc: "Resist the urge to form a fast verdict under ambiguity. Withhold judgment until sufficient evidence is collected."
          }, {
            title: "12. Revising Beliefs as Strength",
            desc: "Updating confidence in response to new disconfirming evidence is the definition of intellectual strength, never weakness."
          }].map((skill, idx) => <div key={idx} className="p-3.5 rounded-lg border border-border/50 bg-card/30 flex flex-col justify-start">
                <span className="font-serif text-xs font-bold text-foreground mb-1">
                  {skill.title}
                </span>
                <p className="font-sans text-xs text-muted-foreground leading-relaxed">
                  {skill.desc}
                </p>
              </div>)}
          </div>
        </LessonStep>

        {/* Step 3: Crucial Epistemic Principle */}
        <LessonStep kind="analysis" title="The Crucial Difference: 'False' vs 'Not Established'">
          <div className="space-y-3">
            <p>
              One of the most widespread errors in everyday discourse is confusing <strong className="text-foreground">"the claim is false"</strong> with <strong className="text-foreground">"the claim has not been established"</strong>.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border border-border bg-card/40">
                <div className="flex items-center gap-2 mb-2">
                  <StatusBadge status="unsupported" compact />
                  <span className="font-serif text-sm font-bold text-foreground">
                    "Not Established" (Suspension of Judgment)
                  </span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Means the claimant has not provided sufficient reliable evidence or valid deductive inference to meet their <Term id="burden-of-proof" />. It does <em>not</em> prove the proposition is physically impossible or false; it means we do not yet have warrant to accept it as true.
                </p>
              </div>

              <div className="p-4 rounded-lg border border-border bg-card/40">
                <div className="flex items-center gap-2 mb-2">
                  <StatusBadge status="empirical" compact />
                  <span className="font-serif text-sm font-bold text-foreground">
                    "Demonstrated False" (Active Contradiction)
                  </span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Means decisive positive evidence or strict logical contradiction has been demonstrated that actively refutes the claim. Saying a claim is false carries its own burden of proof.
                </p>
              </div>
            </div>

            <div className="p-4 rounded-lg border border-primary/30 bg-primary/5 flex items-start gap-3 mt-2">
              <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
              <div className="text-xs sm:text-sm text-foreground/90 space-y-1">
                <strong>"Insufficient Information" is a Skilful Conclusion:</strong>
                <p className="text-muted-foreground">
                  In school exams, answering "I don't have enough data to decide" is often penalized. In the real world of science, law, engineering, and medicine, concluding <strong className="text-foreground">"insufficient information"</strong> is often the highest mark of professional integrity. It prevents costly, catastrophic decisions based on premature speculation.
                </p>
              </div>
            </div>
          </div>
        </LessonStep>

        {/* Step 4: Common Mistake */}
        <LessonStep kind="mistake" title="The Trap of the Golden Mean and the Cynic's Mirror">
          <div className="space-y-2">
            <p>
              Two complementary cognitive errors derail critical discussions:
            </p>
            <ul className="text-xs sm:text-sm text-muted-foreground space-y-2 list-disc pl-5">
              <li>
                <strong className="text-foreground">The Golden Mean Fallacy:</strong> Assuming that because two parties disagree, the truth must lie exactly halfway between them. If one person claims the Earth is a sphere and another claims it is flat, the truth is not that the Earth is a cylinder or a gentle oval. Sometimes one position is empirically established and the other is baseless.
              </li>
              <li>
                <strong className="text-foreground">The Cynic's Mirror:</strong> Believing that discovering a <Term id="fallacy" /> in an opponent's speech automatically proves their conclusion is false (the <Term id="fallacy" /> fallacy or argumentum ad logicam). A person can present a terrible, fallacious argument for a true conclusion (e.g. "It rains because the sky is crying"). Dismantling their bad reasoning leaves the conclusion unproven by them—it does not prove the rain does not exist.
              </li>
            </ul>
          </div>
        </LessonStep>

        {/* Step 5: Deeper Explorations */}
        <Deeper title="The Principle of Charity & Steelmanning in Practice">
          <div className="space-y-4 text-xs sm:text-sm">
            <p>
              When evaluating an argument with which you disagree, the instinct is often to attack its weakest sentence, mock a clumsy phrasing, or caricature the speaker (creating a <Term id="straw-man" />).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border border-border/50 bg-card/30">
                <h5 className="font-serif text-sm font-bold text-foreground mb-1.5 flex items-center gap-2">
                  <Scale className="size-4 text-primary" />
                  The Principle of Charity
                </h5>
                <p className="text-muted-foreground leading-relaxed">
                  Interpreting a speaker's statements in the strongest, most rational, and most coherent way compatible with their words. Do not assume malice or sheer stupidity where ambiguity allows a reasonable reading.
                </p>
              </div>

              <div className="p-4 rounded-lg border border-border/50 bg-card/30">
                <h5 className="font-serif text-sm font-bold text-foreground mb-1.5 flex items-center gap-2">
                  <ShieldAlert className="size-4 text-primary" />
                  Steelmanning
                </h5>
                <p className="text-muted-foreground leading-relaxed">
                  The active practice of formulating the <Term id="steelmanning" /> version of your opponent's position—repairing their missing premises, providing the best available evidence, and removing rhetorical distractions—before you attempt to critique it.
                </p>
              </div>
            </div>
            <p className="text-muted-foreground italic">
              "If you cannot articulate the opposing viewpoint so clearly and persuasively that its supporters say 'Yes, that is exactly what I mean,' you have not yet earned the right to criticize it."
            </p>
          </div>
        </Deeper>

        {/* Step 6: Practical Application */}
        <LessonStep kind="application" title="Distinguishing Criticism of the Argument from Attacking the Person">
          <p>
            Critical thinking enforces an absolute separation between the <strong className="text-foreground">argument</strong> and the <strong className="text-foreground">person making it</strong>:
          </p>
          <div className="overflow-x-auto my-3 max-w-full rounded border border-border/40" tabIndex={0} aria-label="Comparison table of ad-hominem person attacks versus valid structural argument critiques">
            <table className="w-full min-w-[520px] text-left text-xs font-sans border-collapse">
              <thead>
                <tr className="border-b border-border bg-card/50">
                  <th className="p-2.5 font-mono text-primary uppercase">Ad-Hominem Person Attack (Invalid)</th>
                  <th className="p-2.5 font-mono text-primary uppercase">Structural Argument Critique (Valid)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40 text-muted-foreground">
                <tr>
                  <td className="p-2.5">"You only care about bike lanes because you're a trendy hipster with no real job."</td>
                  <td className="p-2.5 text-foreground">"The proposal assumes 34 lost parking spots will zero out retail sales, without examining municipal garage capacity or foot-traffic data."</td>
                </tr>
                <tr>
                  <td className="p-2.5">"The doctor recommending this vaccine works for the healthcare system, so he's obviously corrupt."</td>
                  <td className="p-2.5 text-foreground">"What was the sample size, statistical significance, and conflict-of-interest disclosure in the underlying Phase III trial?"</td>
                </tr>
                <tr>
                  <td className="p-2.5">"You're just stubborn and close-minded."</td>
                  <td className="p-2.5 text-foreground">"What specific empirical observation would lead you to reduce your confidence in this hypothesis?"</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">
            Attacking personal motives is emotionally satisfying but epistemically bankrupt. Even self-interested or flawed individuals can utter sound arguments; saintly and lovable individuals can utter invalid nonsense. Focus exclusively on premises, inference rules, and evidentiary standards.
          </p>
        </LessonStep>
      </div>
    </KSection>;
}